import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Button,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "reactstrap";

import type { AgendaOptionsActionPreference } from "../../../../helpers/agendaUnitContext";

type AgendaOptionsMenuProps = {
  unitSchedulePath: string;
  unitScheduleDisabled?: boolean;
  guideUrl: string;
  lastAction: AgendaOptionsActionPreference | null;
  onRememberAction: (action: AgendaOptionsActionPreference) => void;
  onOpenStatus: () => void;
  onPrintDay: () => void;
  onOpenWaitlist: () => void;
};

const AgendaOptionsMenu = ({
  unitSchedulePath,
  unitScheduleDisabled = false,
  guideUrl,
  lastAction,
  onRememberAction,
  onOpenStatus,
  onPrintDay,
  onOpenWaitlist,
}: AgendaOptionsMenuProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const recentBadge = (action: AgendaOptionsActionPreference) =>
    lastAction === action ? (
      <span className="agenda-options-menu__recent text-muted small ms-2">
        {t("studio.agenda.options.recent")}
      </span>
    ) : null;

  const closeAndRemember = (action: AgendaOptionsActionPreference, run: () => void) => {
    setOpen(false);
    onRememberAction(action);
    run();
  };

  const quickMeta: {
    label: string;
    icon: string;
    disabled?: boolean;
  } | null = (() => {
    switch (lastAction) {
      case "unit_schedule":
        return {
          label: t("studio.agenda.toolbar.unitSchedule"),
          icon: "ri-time-line",
          disabled: unitScheduleDisabled,
        };
      case "guide":
        return {
          label: t("help.articleLink"),
          icon: "ri-book-open-line",
        };
      case "status":
        return {
          label: t("studio.agenda.options.status"),
          icon: "ri-palette-line",
        };
      case "print":
        return {
          label: t("studio.agenda.calendar.printDay"),
          icon: "ri-printer-line",
        };
      case "waitlist":
        return {
          label: t("agenda.waitlist.title"),
          icon: "ri-user-add-line",
        };
      default:
        return null;
    }
  })();

  const runQuickAction = () => {
    if (!lastAction || !quickMeta || quickMeta.disabled) {
      return;
    }

    onRememberAction(lastAction);

    switch (lastAction) {
      case "status":
        onOpenStatus();
        break;
      case "print":
        onPrintDay();
        break;
      case "waitlist":
        onOpenWaitlist();
        break;
      default:
        break;
    }
  };

  return (
    <div className="agenda-options-menu d-flex align-items-center gap-1">
      {lastAction === "unit_schedule" && quickMeta ? (
        <Button
          color="secondary"
          outline
          size="sm"
          tag={Link}
          to={unitSchedulePath}
          disabled={unitScheduleDisabled}
          className="agenda-options-menu__quick bg-white text-body border-secondary-subtle"
          onClick={() => {
            if (unitScheduleDisabled) {
              return;
            }

            onRememberAction("unit_schedule");
          }}
          title={quickMeta.label}
        >
          <i className={`${quickMeta.icon} align-middle me-1`} aria-hidden="true" />
          <span className="agenda-options-menu__quick-label">{quickMeta.label}</span>
        </Button>
      ) : null}
      {lastAction === "guide" && quickMeta ? (
        <Button
          color="secondary"
          outline
          size="sm"
          tag="a"
          href={guideUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="agenda-options-menu__quick bg-white text-body border-secondary-subtle"
          onClick={() => onRememberAction("guide")}
          title={quickMeta.label}
        >
          <i className={`${quickMeta.icon} align-middle me-1`} aria-hidden="true" />
          <span className="agenda-options-menu__quick-label">{quickMeta.label}</span>
        </Button>
      ) : null}
      {lastAction &&
      quickMeta &&
      lastAction !== "unit_schedule" &&
      lastAction !== "guide" ? (
        <Button
          color="secondary"
          outline
          size="sm"
          type="button"
          className="agenda-options-menu__quick bg-white text-body border-secondary-subtle"
          onClick={runQuickAction}
          title={quickMeta.label}
        >
          <i className={`${quickMeta.icon} align-middle me-1`} aria-hidden="true" />
          <span className="agenda-options-menu__quick-label">{quickMeta.label}</span>
        </Button>
      ) : null}
      <Dropdown
        isOpen={open}
        toggle={() => setOpen((current) => !current)}
        className="agenda-options-menu__dropdown"
      >
        <DropdownToggle
          color="secondary"
          outline
          size="sm"
          className="agenda-options-menu__toggle bg-white text-body border-secondary-subtle"
          caret
        >
          <i className="ri-more-2-line align-middle me-1" aria-hidden="true" />
          {t("studio.agenda.options.label")}
        </DropdownToggle>
        <DropdownMenu end container="body" positionFixed>
          <DropdownItem
            tag={Link}
            to={unitSchedulePath}
            disabled={unitScheduleDisabled}
            onClick={() => {
              if (unitScheduleDisabled) {
                return;
              }

              closeAndRemember("unit_schedule", () => undefined);
            }}
          >
            {t("studio.agenda.toolbar.unitSchedule")}
            {recentBadge("unit_schedule")}
          </DropdownItem>
          <DropdownItem
            tag="a"
            href={guideUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => closeAndRemember("guide", () => undefined)}
          >
            {t("help.articleLink")}
            {recentBadge("guide")}
          </DropdownItem>
          <DropdownItem onClick={() => closeAndRemember("status", onOpenStatus)}>
            {t("studio.agenda.options.status")}
            {recentBadge("status")}
          </DropdownItem>
          <DropdownItem onClick={() => closeAndRemember("print", onPrintDay)}>
            {t("studio.agenda.calendar.printDay")}
            {recentBadge("print")}
          </DropdownItem>
          <DropdownItem divider />
          <DropdownItem onClick={() => closeAndRemember("waitlist", onOpenWaitlist)}>
            {t("agenda.waitlist.title")}
            {recentBadge("waitlist")}
          </DropdownItem>
        </DropdownMenu>
      </Dropdown>
    </div>
  );
};

export default AgendaOptionsMenu;
